import { useEffect, useState } from "react";
import { Mic2, Bell, LogOut, Menu, X, Search, Home, Users, ClipboardCheck, FileText, Heart, BarChart3, Settings, Shield, Music4, ListChecks, Wallet, ScrollText, Send, FileSignature, Link2, Headphones } from "lucide-react";
import { http, fmtDateTime } from "../api";

const NAV = {
  admin: [
    { id: "dashboard", label: "Dashboard", icon: Home },
    { id: "samples", label: "Sample Review", icon: ClipboardCheck },
    { id: "artists", label: "Artists", icon: Users },
    { id: "clients", label: "Clients", icon: Users },
    { id: "creators", label: "AI Audiobook Creators", icon: Headphones },
    { id: "orders", label: "Orders", icon: FileText },
    { id: "reports", label: "Reports", icon: BarChart3 },
    { id: "agreements", label: "Agreements", icon: FileSignature },
    { id: "settings", label: "Telegram Alerts", icon: Send },
    { id: "access", label: "Admin Access", icon: Shield },
    { id: "communications", label: "Manage Communication", icon: ListChecks },
    { id: "audit", label: "Audit Log", icon: ScrollText },
    { id: "pitch-links", label: "Generated Links", icon: Link2 },
  ],
  artist: [
    { id: "dashboard", label: "Overview", icon: Home },
    { id: "samples", label: "My Samples", icon: Music4 },
    { id: "pitch-links", label: "Pitch Links", icon: Link2 },
    { id: "orders", label: "My Orders", icon: FileText },
    { id: "profile", label: "Profile", icon: Settings },
    { id: "payment", label: "Payment Details", icon: Wallet },
    { id: "telegram", label: "Telegram Alerts", icon: Send },
  ],
  client: [
    { id: "marketplace", label: "Marketplace", icon: Search },
    { id: "shortlist", label: "My Shortlist", icon: Heart },
    { id: "orders", label: "My Orders", icon: FileText },
  ],
  creator: [
    { id: "journey", label: "Hiring Journey", icon: ListChecks },
  ],
};

function Notifications({ open, onClose, notifs, refresh }) {
  if (!open) return null;
  const markRead = async (n) => {
    if (!n.read) await http.post(`/notifications/${n.id}/read`);
    refresh();
  };
  const markAll = async () => { await http.post("/notifications/read-all"); refresh(); };
  return (
    <>
      <div className="nav-backdrop show" onClick={onClose} />
      <div className="notif-panel" data-testid="notifications-panel">
        <div className="notif-panel-head">
          <strong style={{ fontSize: 14 }}>Notifications</strong>
          <button className="ghost" onClick={markAll} data-testid="mark-all-read">Mark all read</button>
        </div>
        {notifs.length === 0 && <div className="notif-item">No notifications yet.</div>}
        {notifs.map(n => (
          <div key={n.id} className={`notif-item ${n.read ? "" : "unread"}`} onClick={() => markRead(n)} data-testid={`notif-${n.id}`}>
            {n.message}
            <small>{fmtDateTime(n.created_at)}</small>
          </div>
        ))}
      </div>
    </>
  );
}

export default function AppShell({ user, page, setPage, onLogout, children }) {
  const [navOpen, setNavOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const items = NAV[user.role] || [];

  const loadNotifs = () => http.get("/notifications").then(r => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); const i = setInterval(loadNotifs, 30000); return () => clearInterval(i); }, []);
  const unread = notifs.filter(n => !n.read).length;

  return (
    <>
      <header className="topbar">
        <div className="top-left">
          <button className="menu-btn" onClick={() => setNavOpen(true)} data-testid="mobile-menu-btn"><Menu size={18} /></button>
          <div className="brand"><span className="brand-mark"><Mic2 size={18} /></span> vocalhouse</div>
        </div>
        <div className="top-actions">
          <span className="role-pill" data-testid="user-role-label">
            {user.role === "admin" ? <><Shield size={12} style={{ display: "inline", marginRight: 4 }} />Admin</> : user.role === "creator" ? "AI Audiobook Creator" : `${user.role} workspace`}
          </span>
          <button className="icon-btn" onClick={() => setNotifOpen(v => !v)} data-testid="notifications-btn">
            <Bell size={17} />{unread > 0 && <span className="notif-dot" />}
          </button>
          <button className="icon-btn" onClick={onLogout} data-testid="logout-button" title="Logout"><LogOut size={17} /></button>
          <Notifications open={notifOpen} onClose={() => setNotifOpen(false)} notifs={notifs} refresh={loadNotifs} />
        </div>
      </header>
      <div className="app-shell">
        <div className={`nav-backdrop ${navOpen ? "show" : ""}`} onClick={() => setNavOpen(false)} />
        <aside className={navOpen ? "open" : ""}>
          <div className="nav-section">Workspace</div>
          {items.map(item => {
            const Icon = item.icon;
            return (
              <button key={item.id} data-testid={`nav-${item.id}`} className={`nav-btn ${page === item.id ? "active" : ""}`} onClick={() => { setPage(item.id); setNavOpen(false); }}>
                <Icon size={16} /> {item.label}
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <div className="nav-section">Session</div>
          <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--muted)" }}>
            <div style={{ fontWeight: 600, color: "var(--ink)" }}>{user.name}</div>
            <div style={{ fontSize: 11 }}>{user.email}</div>
          </div>
        </aside>
        <main className="content">{children}</main>
      </div>
    </>
  );
}
